// filepath: /Users/nacho/Development/sme-saas-debt/sme-saas-debt-front/src/shared/ui/ToastProvider/ToastProvider.tsx
import React, { createContext, useContext, ReactNode } from 'react';
import { SnackbarProvider, useSnackbar, VariantType, OptionsObject, SnackbarKey } from 'notistack';

interface ToastContextType {
  success: (message: string, options?: OptionsObject) => SnackbarKey;
  error: (message: string, options?: OptionsObject) => SnackbarKey;
  warning: (message: string, options?: OptionsObject) => SnackbarKey;
  info: (message: string, options?: OptionsObject) => SnackbarKey;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
  maxSnack?: number;
  anchorOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
}

// The actual provider component that manages all toasts
const ToastContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();

  // Create helper functions for each toast type
  const showToast = (message: string, variant: VariantType = 'default', options?: OptionsObject) => {
    return enqueueSnackbar(message, {
      variant,
      ...options
    });
  };

  const success = (message: string, options?: OptionsObject) => showToast(message, 'success', options);
  const error = (message: string, options?: OptionsObject) => showToast(message, 'error', options);
  const warning = (message: string, options?: OptionsObject) => showToast(message, 'warning', options);
  const info = (message: string, options?: OptionsObject) => showToast(message, 'info', options);

  const contextValue = {
    success,
    error,
    warning,
    info
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
    </ToastContext.Provider>
  );
};

// The exported provider that sets up notistack and our context
export const ToastProvider: React.FC<ToastProviderProps> = ({ 
  children,
  maxSnack = 3,
  anchorOrigin = { vertical: 'bottom', horizontal: 'right' }
}) => {
  return (
    <SnackbarProvider 
      maxSnack={maxSnack}
      anchorOrigin={anchorOrigin}
    >
      <ToastContextProvider>
        {children}
      </ToastContextProvider>
    </SnackbarProvider>
  );
};

// Custom hook to use the toast context
export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
